/*
 * jrs-v2-pdp-selection-store.js — always-on persistence of the PDP builder
 * selection (garments / sizes / quantities), independent of the customizer.
 *
 * Why: the customizer only ever saved the *branding* draft; the selection it
 * belonged to was read live from the DOM at Confirm and never persisted. This
 * module saves the selection continuously so it survives a reload even if the
 * customer never opens the customizer — the "Resume selections" half of the PDP
 * resume feature (jrs-v2-pdp-resume.js) reads it back.
 *
 * Storage: localStorage `jrs.pdp.selection.v1`, a single product-scoped slot
 *   { version:1, productHandle, flow, selection, savedAt }
 * Latest write wins. Reads are product-scoped (a selection saved on product A
 * never surfaces on product B). Empty selections are never written (so a
 * transient empty read during a builder re-render can't wipe a good save), and
 * the slot is cleared on a committed add-to-cart.
 *
 * The selection shape comes from JRS.PdpSelection.snapshot() (exposed by
 * jrs-v2-pdp-trigger.liquid) — the single source of truth for reading the live
 * builder state.
 */
window.JRS = window.JRS || {};

(function (JRS) {
  'use strict';

  var KEY = 'jrs.pdp.selection.v1';
  var DEBOUNCE_MS = 600;
  var _timer = null;
  var _suppressed = false;
  // Latched true from a committed add-to-cart until the next customization
  // session opens. While latched, auto-save is a no-op so the still-open
  // success popup + live builder DOM can't re-create the just-cleared slot
  // (the "reload after confirm still shows Resume, and Resume is blank" bug).
  var _committed = false;

  function _handle() {
    return (window.JRS_PDP_CONTEXT && window.JRS_PDP_CONTEXT.productHandle) || null;
  }

  function _isEmpty(sel) {
    if (!sel) return true;
    if (sel.flow === 'bundle') return !(sel.bundle_children && sel.bundle_children.length);
    return !(sel.sizes && sel.sizes.length);
  }

  // Read the raw stored record IF it belongs to the current product. null otherwise.
  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return null;
      var rec = JSON.parse(raw);
      if (!rec || rec.version !== 1) return null;
      var here = _handle();
      if (!rec.productHandle || !here || rec.productHandle !== here) return null;
      if (_isEmpty(rec.selection)) return null;
      return rec;
    } catch (e) { return null; }
  }

  function saveNow(opts) {
    if (_suppressed || _committed) return;
    if (!JRS.PdpSelection || typeof JRS.PdpSelection.snapshot !== 'function') return;
    var here = _handle();
    if (!here) return;
    var sel;
    try { sel = JRS.PdpSelection.snapshot(); } catch (e) { return; }
    // Never overwrite a good save with an empty one (protects against transient
    // empty reads while a bundle grid re-renders). Genuine clears are handled by
    // clear() on add-to-cart / Start fresh, not here.
    if (_isEmpty(sel)) return;
    var explicit = !!(opts && opts.explicit);
    // Ruling 2026-09-01: non-bundle selections persist ONLY on an explicit
    // "Save and close". Bundle selections keep the always-on save — they are
    // what the unsaved-state pill restores ("bundle selections only").
    if (!explicit && sel.flow !== 'bundle') return;
    var saveType = explicit ? 'explicit' : 'bundle_only';
    if (!explicit) {
      // An auto-save after an explicit save must not downgrade the record.
      var prev = read();
      if (prev && prev.saveType === 'explicit') saveType = 'explicit';
    }
    try {
      localStorage.setItem(KEY, JSON.stringify({
        version: 1,
        productHandle: here,
        flow: sel.flow || null,
        saveType: saveType,
        selection: sel,
        savedAt: Date.now()
      }));
    } catch (e) { /* quota / private mode — fail silent */ }
  }

  // Explicit "Save and close" (the customizer's save-prompt save action):
  // persists the selection regardless of flow, marked saveType 'explicit'.
  function saveExplicit() {
    saveNow({ explicit: true });
  }

  function saveDebounced() {
    if (_suppressed || _committed) return;
    if (_timer) clearTimeout(_timer);
    _timer = setTimeout(saveNow, DEBOUNCE_MS);
  }

  function clear() {
    if (_timer) { clearTimeout(_timer); _timer = null; }
    try { localStorage.removeItem(KEY); } catch (e) {}
  }

  // Silence auto-save while the resume feature programmatically re-drives the
  // builder (its change events would otherwise re-trigger a save mid-restore).
  function suppress(v) {
    _suppressed = !!v;
    if (_suppressed && _timer) { clearTimeout(_timer); _timer = null; }
  }

  function _onBuilderActivity() {
    if (_suppressed) return;
    saveDebounced();
  }

  function _init() {
    // Bundle: the vendor app keeps `.mrk-qty-item` live on every quantity change.
    // Multistep: quantities live in the <pdp-multistep-form-v2> element's state,
    //   surfaced into hidden inputs on demand by the snapshot's _syncHiddenInputs
    //   call — so we listen for any interaction within the form element.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t) return;
      if (t.classList && t.classList.contains('mrk-qty-item')) return _onBuilderActivity();
      if (t.closest && t.closest('pdp-multistep-form-v2')) return _onBuilderActivity();
    }, true);
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('pdp-multistep-form-v2, [data-jrs-product-card], .bundle-selector')) {
        _onBuilderActivity();
      }
    }, true);
    document.addEventListener('input', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('pdp-multistep-form-v2')) return _onBuilderActivity();
    }, true);

    // A committed add-to-cart (the customizer confirm path fires this via
    // CartAPI) consumes the current selection: clear the slot AND latch
    // _committed so the still-open success popup + live builder DOM can't
    // re-create it via a debounced auto-save. The latch releases when a new
    // customization session opens (customizer:opened), re-enabling persistence
    // for the next order.
    if (JRS.EventBus && typeof JRS.EventBus.on === 'function') {
      JRS.EventBus.on('cart:addSucceeded', function () {
        _committed = true;
        clear();
      });
      JRS.EventBus.on('customizer:opened', function () {
        _committed = false;
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _init);
  } else {
    _init();
  }

  JRS.PdpSelectionStore = {
    read: read,
    saveNow: saveNow,
    saveExplicit: saveExplicit,
    saveDebounced: saveDebounced,
    clear: clear,
    suppress: suppress,
    _isEmpty: _isEmpty, // test seam
    _setCommitted: function (v) { _committed = !!v; } // test seam
  };
})(window.JRS);
